import { useEffect, useMemo, useState } from 'react'

import { Button, Modal, Trans } from '@/components'

import { ConfirmAssignmentDialog } from './ConfirmAssignmentDialog'

export type MemberTarget = { id: string; name: string; currentCredit: number }

type Props = {
  open: boolean
  variant: 'bulk' | 'single'
  adminBalance: number
  targets: MemberTarget[] // bulk: multiple；single: 1
  defaultAmount?: number
  onClose: () => void
  onConfirm: (payload: {
    perMemberAmount: number
    memberIds: string[]
    totalCost: number
  }) => Promise<void> | void
}

function formatCredits(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function AssignCreditDialog({
  open,
  variant,
  adminBalance,
  targets,
  defaultAmount = 0,
  onClose,
  onConfirm,
}: Props) {
  const [amount, setAmount] = useState<string>(defaultAmount ? String(defaultAmount) : '')
  const [showConfirm, setShowConfirm] = useState(false)

  useEffect(() => {
    if (open) {
      setAmount(defaultAmount ? String(defaultAmount) : '')
      setShowConfirm(false)
    }
  }, [open, defaultAmount])

  const adminBalanceCredits = useMemo(() => (adminBalance ?? 0) / 100, [adminBalance])

  const memberNames = useMemo(() => targets.map((t) => t.name).join(','), [targets])

  const amountNum = useMemo(() => {
    const v = Number(amount)
    if (Number.isNaN(v) || v < 0) return 0
    return Math.floor(v * 1000) / 1000
  }, [amount])

  const totalCost = variant === 'bulk' ? amountNum * targets.length : amountNum
  const enoughBalance = totalCost <= adminBalanceCredits
  const canSubmit = amountNum > 0 && targets.length > 0 && enoughBalance

  const single = targets[0]

  const afterSingleCredits =
    variant === 'single' && single ? (single.currentCredit ?? 0) / 100 + amountNum : 0

  function sanitizeTo2dp(input: string) {
    let x = input.replace(/[^\d.]/g, '')

    const firstDot = x.indexOf('.')
    if (firstDot !== -1) {
      x = x.slice(0, firstDot + 1) + x.slice(firstDot + 1).replace(/\./g, '')
    }

    const [int = '', frac = ''] = x.split('.')
    const frac2 = frac.slice(0, 2)

    if (firstDot === -1) return int
    if (int === '' && frac2 === '') return ''
    return `${int || '0'}.${frac2}`
  }

  async function handleFinalConfirm() {
    await onConfirm({
      perMemberAmount: amountNum,
      memberIds: targets.map((t) => t.id),
      totalCost,
    })
    setShowConfirm(false)
  }

  return (
    <Modal open={open} onClose={onClose} className="w-[640px]">
      <Modal.Header>
        {variant === 'bulk' ? (
          <Trans i18nKey="team.member.dialog.assignCredit">Assign Credit</Trans>
        ) : (
          <div className="flex items-center gap-2">
            <Trans i18nKey="team.member.dialog.assognCreditTo">Assign Credit to</Trans>{' '}
            <span className="text-primary">{single?.name}</span>
          </div>
        )}
      </Modal.Header>

      <Modal.Body>
        {/* Top card */}
        {variant === 'bulk' ? (
          <div className="rounded-2xl p-6 mb-4 text-center bg-gradient-to-br from-highlight/10 via-btn-secondary/10 to-panel-gray-100">
            <div className="t-sm text-secondary mb-4">
              <Trans i18nKey="team.member.dialog.yourBalance">Your Balance</Trans>
            </div>
            <div className="flex justify-center items-baseline mt-4">
              <span className="t-sb text-primary invisible">Credit</span>
              <span className="text-primary h4 mx-2">{formatCredits(adminBalanceCredits)}</span>
              <span className="t-sb text-primary">
                <Trans i18nKey="team.member.dialog.credit">Credit</Trans>
              </span>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl p-6 mb-4 bg-neutral-50 text-primary">
            <div className="t-lg mb-4">
              <Trans
                i18nKey="team.member.dialog.nameBalanceCredit"
                values={{ name: single?.name ?? '' }}
              >
                {`${single?.name ?? ''}`}&apos;s Balance (Credit)
              </Trans>
            </div>
            <div className="grid grid-cols-12">
              <div className="text-center col-span-5">
                <div className="h3">{formatCredits((single?.currentCredit ?? 0) / 100)}</div>
                <div className="mt-2 t-sb">
                  <Trans i18nKey="team.member.dialog.current">Current</Trans>
                </div>
              </div>
              <div className="col-span-2 flex items-center justify-center py-4">
                <div className="w-px h-full bg-neutral-200"></div>
              </div>
              <div className="text-center col-span-5">
                <div className="h3">{formatCredits(afterSingleCredits)}</div>
                <div className="mt-2 t-sb">
                  <Trans i18nKey="team.member.dialog.after">After</Trans>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Members（bulk only） */}
        {variant === 'bulk' && (
          <div className="mb-4">
            <div className="t-sb text-primary mb-2">
              <Trans i18nKey="team.member.dialog.members">Members</Trans>{' '}
              <span className="text-secondary">{targets.length}</span>
            </div>
            <input
              disabled
              value={memberNames}
              className="w-full h-11 px-4 rounded-lg border border-neutral-200 bg-panel-white/60 text-primary cursor-not-allowed"
              aria-label="Selected members"
            />
          </div>
        )}

        {/* Assign Credit */}
        <div className="mb-1">
          <div className="t-sb text-primary mb-2 flex items-center gap-2">
            <Trans i18nKey="team.member.dialog.assignCredit">Assign Credit</Trans>
            {variant === 'single' && (
              <span className="ml-auto t-sm text-secondary bg-neutral-100 px-4 py-1 rounded-xl">
                <Trans i18nKey="team.member.dialog.balance">Balance</Trans>{' '}
                <span className="text-btn-primary text-highlight">
                  {formatCredits(adminBalanceCredits)}
                </span>{' '}
                <Trans i18nKey="team.member.dialog.credits">Credits</Trans>
              </span>
            )}
          </div>
          <div className="relative">
            <input
              value={amount}
              onChange={(e) => setAmount(sanitizeTo2dp(e.target.value))}
              className="w-full h-11 px-4 pr-20 rounded-lg border border-neutral-200 outline-none focus:border-primary"
              placeholder="0"
              inputMode="decimal"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 t-sb text-secondary">
              <Trans i18nKey="team.member.dialog.credit">Credit</Trans>
            </div>
          </div>
        </div>

        {!enoughBalance && (
          <div className="mt-3 t-sm text-error">
            <Trans i18nKey="team.member.dialog.insufficientBalance">Insufficient balance.</Trans>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {variant === 'bulk' && (
          <div className="mr-auto">
            <div className="h4 text-highlight">
              {formatCredits(totalCost)} <Trans i18nKey="team.member.dialog.credits">Credits</Trans>
            </div>
            <div className="t-xs text-tertiary">
              <Trans i18nKey="team.member.dialog.totalCost">Total Cost</Trans>
            </div>
          </div>
        )}
        <Button type="outline" className="min-w-[88px]" onClick={onClose}>
          <Trans i18nKey="common.cancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          className="min-w-[88px]"
          disabled={!canSubmit}
          onClick={() => setShowConfirm(true)}
        >
          {variant === 'bulk' ? (
            <Trans i18nKey="common.confirm">Confirm</Trans>
          ) : (
            <Trans i18nKey="team.member.dialog.assign">Assign</Trans>
          )}
        </Button>
      </Modal.Footer>

      <ConfirmAssignmentDialog
        open={showConfirm}
        variant={variant}
        targets={targets}
        perMemberAmount={amountNum}
        totalCost={totalCost}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleFinalConfirm}
      />
    </Modal>
  )
}
